import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import StatusPill from '../components/StatusPill'
import { Copy, Eye, FileText, Link } from 'lucide-react'
import { supabase } from '../lib/supabase'
import PaymentRequestEmail from '../components/PaymentRequestEmail'
import { useAuth } from '../lib/AuthContext'
import { newestJobFirst, workshopRowsResult } from '../lib/customerDirectory'
import { isOutboundTestMode } from '../lib/stagingEnvironment'
import {
  JOB_FINANCIAL_SELECT,
  bookingJobSummary,
  fmtDate,
  isGeneralJob,
  jobFinancials,
  jobModuleLabel,
  jobPaymentActionState,
  jobPaymentMeta,
  jobPriceStatus,
  jobServiceSummary,
  jobStatusMeta,
  normaliseJobStatus,
} from '../lib/utils'
import ViewJobModal from '../components/ViewJobModal'
import CombinedPayments from '../components/CombinedPayments'

const PAYMENT_TABS = [
  ['needs_request', 'Needs request'],
  ['requested', 'Requested / unpaid'],
  ['paid', 'Paid'],
  ['problem', 'Pricing / payment review'],
  ['all', 'All'],
]

const STANDARD_CONNECT_MESSAGE = 'Stripe card payments use the workshop connected Stripe Standard account. Bank transfer and manual paid tracking remain available in the job.'

function dayOnly(value) {
  return value ? String(value).slice(0, 10) : ''
}

function ageLabel(value) {
  const day = dayOnly(value)
  if (!day) return ''
  const then = new Date(`${day}T12:00:00`)
  const now = new Date()
  const diff = Math.max(0, Math.floor((now - then) / 86400000))
  if (diff === 0) return 'today'
  if (diff === 1) return '1 day'
  return `${diff} days`
}

function customerName(customer) {
  if (!customer) return 'Unknown customer'
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ') || 'Unnamed customer'
}

function componentName(component) {
  if (!component) return 'No component linked'
  return [component.year, component.brand, component.model].filter(Boolean).join(' ') || 'Component record'
}

function jobDisplayName(job, component) {
  if (isGeneralJob(job)) {
    return jobServiceSummary(job) || bookingJobSummary(job) || 'General Workshop job'
  }
  return componentName(component)
}

function tabForJob(job) {
  if (normaliseJobStatus(job.status) === 'cancelled') return 'problem'
  if (isGeneralJob(job) && jobPriceStatus(job) !== 'final') return 'problem'
  const payment = jobPaymentMeta(job)
  if (payment.key === 'failed' || payment.key === 'cancelled' || payment.key === 'requires_review') return 'problem'
  if (payment.key === 'not_requested') return 'needs_request'
  return payment.key
}

export default function Payments() {
  const { workshopId, workshop } = useAuth()
  const outboundTestMode = isOutboundTestMode()
  const [jobs, setJobs] = useState([])
  const [customers, setCustomers] = useState([])
  const [components, setComponents] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('needs_request')
  const [busyJobId, setBusyJobId] = useState('')
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [viewJobId, setViewJobId] = useState(null)
  const [loadError, setLoadError] = useState('')
  const loadedRef = useRef(false)

  const load = useCallback(async () => {
    if (!workshopId) return
    if (!loadedRef.current) setLoading(true)
    try {
      const [jobRows, customerRows, componentRows] = await Promise.all([
        workshopRowsResult(supabase, 'jobs', workshopId, JOB_FINANCIAL_SELECT, newestJobFirst),
        workshopRowsResult(supabase, 'customers', workshopId),
        workshopRowsResult(supabase, 'components', workshopId),
      ])
      const failed = [jobRows, customerRows, componentRows].find(result => result?.error)
      if (failed?.error) throw failed.error
      setJobs(jobRows.data || [])
      setCustomers(customerRows.data || [])
      setComponents(componentRows.data || [])
      loadedRef.current = true
      setLoadError('')
    } catch (err) {
      setLoadError(err.message || 'Could not refresh payments.')
    } finally {
      setLoading(false)
    }
  }, [workshopId])

  useEffect(() => { load() }, [load])

  const customersById = useMemo(() => new Map(customers.map(customer => [customer.id, customer])), [customers])
  const componentsById = useMemo(() => new Map(components.map(component => [component.id, component])), [components])

  const counts = useMemo(() => {
    const next = { needs_request: 0, requested: 0, paid: 0, problem: 0, all: jobs.length }
    jobs.forEach(job => {
      const tab = tabForJob(job)
      if (next[tab] !== undefined) next[tab] += 1
    })
    return next
  }, [jobs])

  const visibleJobs = jobs.filter(job => activeTab === 'all' || tabForJob(job) === activeTab)
  const queueTotals = useMemo(() => PAYMENT_TABS.reduce((totals, [key]) => {
    totals[key] = jobs
      .filter(job => key === 'all' || tabForJob(job) === key)
      .reduce((sum, job) => sum + jobFinancials(job).totalCost, 0)
    return totals
  }, {}), [jobs])
  const totalOutstanding = jobs
    .filter(job => normaliseJobStatus(job.status) !== 'cancelled' && ['not_requested', 'requested', 'failed'].includes(jobPaymentMeta(job).key))
    .reduce((sum, job) => {
      const financials = jobFinancials(job)
      const paymentState = jobPaymentActionState(job, financials)
      return paymentState.blockedReason ? sum : sum + financials.totalCost
    }, 0)
  const viewJob = jobs.find(job => job.id === viewJobId)
  const viewCustomer = viewJob ? customersById.get(viewJob.customer_id) : null
  const viewComponent = viewJob ? componentsById.get(viewJob.component_id) : null

  function updateJob(jobId, fields) {
    setJobs(current => current.map(job => job.id === jobId ? { ...job, ...fields } : job))
  }

  async function sessionToken() {
    const { data } = await supabase.auth.getSession()
    const token = data?.session?.access_token
    if (!token) throw new Error('Please sign in again before taking payment actions.')
    return token
  }

  async function createPaymentLink(job) {
    if (isOutboundTestMode()) {
      setError('Payment provider actions are disabled in test mode.')
      return
    }
    setBusyJobId(job.id)
    setNotice('')
    setError('')
    try {
      const token = await sessionToken()
      const res = await fetch('/.netlify/functions/create-payment-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ jobId: job.id }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not create payment link.')
      updateJob(job.id, {
        payment_link: data.url,
        payment_status: data.payment_status || 'requested',
        payment_requested_at: new Date().toISOString(),
        stripe_checkout_session_id: data.checkout_session_id || job.stripe_checkout_session_id || '',
        payment_amount_pence: data.payment_amount_pence || job.payment_amount_pence || null,
        payment_refresh_required: false,
        payment_refresh_reason: '',
      })
      setNotice('Payment link ready — customer not contacted.')
    } catch (err) {
      setError(err.message || 'Could not create payment link.')
    } finally {
      setBusyJobId('')
    }
  }

  async function sendReminder(job) {
    if (isOutboundTestMode()) {
      setError('Outbound payment messages are disabled in test mode.')
      return
    }
    if (job.payment_refresh_required) {
      setError(job.payment_refresh_reason || 'Generate a new payment link before sending a reminder.')
      return
    }
    const customer = customersById.get(job.customer_id)
    const to = customer?.email || ''
    if (!to) {
      setError('Add a customer email before sending a payment reminder.')
      return
    }
    const firstSend = !job.payment_reminder_sent_at

    setBusyJobId(job.id)
    setNotice('')
    setError('')
    try {
      const token = await sessionToken()
      const res = await fetch('/.netlify/functions/send-payment-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ jobId: job.id, to }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Payment reminder failed.')
      updateJob(job.id, {
        payment_status: 'requested',
        payment_reminder_sent_at: data.payment_reminder_sent_at || new Date().toISOString(),
        report_sent_at: data.report_sent_at || new Date().toISOString(),
      })
      setNotice(firstSend ? `Payment request and report emailed to ${to}.` : `Payment reminder and report emailed to ${to}.`)
      if (data.warning) setError(data.warning)
    } catch (err) {
      setError(err.message || 'Payment reminder failed.')
    } finally {
      setBusyJobId('')
    }
  }

  async function copyPaymentLink(job) {
    if (!job.payment_link || job.payment_refresh_required) return
    try {
      await navigator.clipboard.writeText(job.payment_link)
      setNotice('Payment link copied.')
      setError('')
    } catch {
      setError('Could not copy payment link in this browser.')
    }
  }

  if (loading && !loadedRef.current) return <div className="empty"><span className="spinner" style={{ width: 20, height: 20 }} /></div>
  if (loadError && !loadedRef.current) return (
    <div className="empty">
      <p>{loadError}</p>
      <button type="button" className="btn btn-primary" onClick={load}>Retry</button>
    </div>
  )

  return (
    <div className="payments-refresh-page">
      <div className="page-head payments-refresh-head">
        <div>
          <div className="refresh-kicker">Job balances</div>
          <div className="page-title">Payments</div>
          <div className="text-muted fs12 mt4">Clear balances, safe requests and a traceable handover.</div>
        </div>
      </div>

      {loadError && loadedRef.current && <div className="alert alert-error payments-refresh-load-error" role="alert"><span>{loadError} The current queue and open job have been kept.</span><button type="button" className="btn btn-ghost btn-sm" onClick={load}>Retry</button></div>}

      <div className="payments-refresh-summary">
        {PAYMENT_TABS.map(([key, label]) => (
          <button key={key} type="button" aria-pressed={activeTab === key} onClick={() => setActiveTab(key)}>
            <span>{label} · {counts[key] || 0}</span>
            <strong>£{queueTotals[key].toFixed(0)}</strong>
          </button>
        ))}
        <div className="payments-refresh-outstanding"><span>Eligible outstanding</span><strong>£{totalOutstanding.toFixed(0)}</strong></div>
      </div>

      <div className="alert alert-info">{STANDARD_CONNECT_MESSAGE}</div>
      {outboundTestMode && <div className="alert alert-info">Test mode: payment provider actions and outbound reminders are disabled.</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <CombinedPayments workshopId={workshopId} customersById={customersById} jobs={jobs} disabled={outboundTestMode} onChanged={load} />

      {!visibleJobs.length ? (
        <div className="empty">
          <div className="empty-icon">£</div>
          <p>No jobs in this payment queue.</p>
        </div>
      ) : (
        <div className="card payments-refresh-list" style={{ padding: 0 }}>
          <table className="payment-table">
            <thead>
              <tr>
                <th>Customer</th><th>Job</th><th>Amount</th><th>Status</th><th>Dates</th><th></th>
              </tr>
            </thead>
            <tbody>
              {visibleJobs.map(job => {
                const customer = customersById.get(job.customer_id)
                const component = componentsById.get(job.component_id)
                const payment = jobPaymentMeta(job)
                const status = jobStatusMeta(job)
                const financials = jobFinancials(job)
                const paymentState = jobPaymentActionState(job, financials)
                const summary = jobServiceSummary(job) || bookingJobSummary(job) || job.notes || ''
                const busy = busyJobId === job.id
                const activePaymentLink = paymentState.activePaymentLink
                const canCreateLink = paymentState.canCreateLink
                const canCopyLink = paymentState.canCopyLink
                const canRemind = paymentState.canSendReminder
                const linkSent = Boolean(job.payment_reminder_sent_at)
                const sendLabel = linkSent ? 'Reminder & Report' : 'Email Payment Request & Report'
                return (
                  <tr key={job.id}>
                    <td style={{ verticalAlign: 'top' }}>
                      <div className="text-white fw7">{customerName(customer)}</div>
                      <div className="fs11 text-muted">{customer?.email || 'No email'}</div>
                    </td>
                    <td style={{ verticalAlign: 'top', minWidth: 0 }}>
                      <div className="text-white fw7">{jobDisplayName(job, component)}</div>
                      {summary && <div className="fs12 text-muted payment-summary-text">{summary}</div>}
                      <StatusPill tone={status.tone}>{status.label}</StatusPill>
                      <span className="job-status-chip job-status-blue" style={{ marginLeft: 6 }}>{jobModuleLabel(job)}</span>
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <div className="text-white fw7">
                        {financials.unresolvedChargeableStockCount > 0 ? 'Review stock' : `£${financials.totalCost.toFixed(2)}`}
                      </div>
                      {financials.unresolvedChargeableStockCount > 0 && (
                        <div className="fs11 text-yellow">Chargeable draft stock unresolved</div>
                      )}
                      {job.invoice_number && <div className="mono fs11 text-muted">{job.invoice_number}</div>}
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <StatusPill tone={payment.tone}>{payment.label}</StatusPill>
                      {payment.key !== 'paid' && !activePaymentLink && !job.payment_refresh_required && (
                        <div className="fs11 text-muted mt4">No payment link yet</div>
                      )}
                      {activePaymentLink && payment.key !== 'paid' && (
                        <div className="fs11 text-muted mt4">
                          {linkSent ? 'Stripe Standard link ready' : 'Payment link ready — customer not contacted'}
                        </div>
                      )}
                      {paymentState.refreshReason && (
                        <div className="fs11 text-yellow mt4">{paymentState.refreshReason}</div>
                      )}
                      {paymentState.blockedReason && payment.key !== 'paid' && (
                        <div className="fs11 text-muted mt4">{paymentState.blockedReason}</div>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <div className="fs12 text-muted">Job {fmtDate(dayOnly(job.date))}</div>
                      {job.payment_requested_at && (
                        <div className="fs11 text-muted">Requested {ageLabel(job.payment_requested_at)} ago</div>
                      )}
                      {job.payment_reminder_sent_at && (
                        <div className="fs11 text-muted">Reminder {fmtDate(dayOnly(job.payment_reminder_sent_at))}</div>
                      )}
                      {job.payment_paid_at && (
                        <div className="fs11 text-green">Paid {fmtDate(dayOnly(job.payment_paid_at))}</div>
                      )}
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                      <div className="payment-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => setViewJobId(job.id)}><Eye size={16} aria-hidden="true" />View</button>
                        <PaymentRequestEmail job={job} customer={customer} canCreate={canCreateLink} canSend={canRemind} disabled={busy || outboundTestMode}
                          onBusy={value => { setBusyJobId(value ? job.id : ''); if (value) { setNotice(''); setError('') } }}
                          onResult={({ generated, sent }) => {
                            if (generated) updateJob(job.id, { payment_link: generated.url, payment_status: generated.payment_status || 'requested',
                              stripe_checkout_session_id: generated.checkout_session_id, payment_refresh_required: false, payment_refresh_reason: '' })
                            if (sent) { updateJob(job.id, { payment_reminder_sent_at: sent.payment_reminder_sent_at }); setNotice('Payment request emailed. No report or photos attached.') }
                          }} />
                        {canCreateLink && (
                          <button className="btn btn-ghost btn-sm" onClick={() => createPaymentLink(job)} disabled={busy || outboundTestMode} title={outboundTestMode ? 'Payment provider actions are disabled in test mode.' : undefined}>
                            <Link size={16} aria-hidden="true" />
                            {busy ? 'Working...' : 'Generate Link Only'}
                          </button>
                        )}
                        {canCopyLink && (
                          <button className="btn btn-ghost btn-sm" onClick={() => copyPaymentLink(job)}><Copy size={16} aria-hidden="true" />Copy Link</button>
                        )}
                        {canRemind && (
                          <button className="btn btn-ghost btn-sm" onClick={() => sendReminder(job)} disabled={busy || outboundTestMode} title={outboundTestMode ? 'Outbound payment messages are disabled in test mode.' : 'Emails the payment request with the service report attached.'}>
                            <FileText size={16} aria-hidden="true" />
                            {busy ? 'Sending...' : sendLabel}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewJobId && viewJob && (
        <ViewJobModal
          job={viewJob}
          customer={viewCustomer}
          component={viewComponent}
          workshop={workshop}
          returnLabel="Back to Payments"
          onClose={() => setViewJobId(null)}
          onEdit={null}
          onChanged={load}
        />
      )}
    </div>
  )
}
